// public/js/components/phone-input.js

const CLASSES = {
  valid: ['bg-teal-50', 'border-teal-600', 'focus:ring-teal-700'],
  invalid: ['bg-red-50', 'border-red-600', 'focus:ring-red-600'],
  neutral: ['bg-white', 'border-slate-600', 'focus:ring-teal-700'],
};

export default function PhoneInput(rootEl) {
  const trigger = rootEl.querySelector('[data-action="toggle"]');
  const menu = rootEl.querySelector('[data-ref="menu"]');
  const fullInput = rootEl.querySelector('[data-ref="full-input"]'); 
  const display = rootEl.querySelector('[data-ref="display"]');
  const visualInput = rootEl.querySelector('[data-ref="visual-input"]');
  const errorDiv = rootEl.querySelector('[data-ref="error"]');
  const searchInput = rootEl.querySelector('[data-ref="search"]');
  const list = rootEl.querySelector('[data-ref="list"]');
  const options = Array.from(rootEl.querySelectorAll('[role="option"]'));

  if (!trigger || !menu || !fullInput || !display || !visualInput || !errorDiv) {
    throw new Error('PhoneInput: Missing required DOM elements');
  }

  let currentPrefix = '';
  let menuIsOpen = false;

  const setVisualState = (state, message = '') => {
    errorDiv.textContent = message;

    [visualInput, trigger].forEach(el => {
      el.classList.remove(
        ...CLASSES.valid,
        ...CLASSES.invalid,
        ...CLASSES.neutral
      );
    });

    if (message) {
      errorDiv.classList.remove('hidden');
    } else {
      errorDiv.classList.add('hidden');
    }

    switch (state) {
      case 'valid':
        visualInput.setAttribute('aria-invalid', 'false');
        visualInput.classList.add(...CLASSES.valid);
        trigger.classList.add(...CLASSES.valid);
        break;

      case 'invalid':
        visualInput.setAttribute('aria-invalid', 'true');
        visualInput.classList.add(...CLASSES.invalid);
        trigger.classList.add(...CLASSES.invalid);
        break;

      default: 
        visualInput.setAttribute('aria-invalid', 'false');
        visualInput.classList.add(...CLASSES.neutral);
        trigger.classList.add(...CLASSES.neutral);
    }
  };

  const syncToMaster = () => {
    const rawValue = visualInput.value.replace(/\s/g, '');
    const rawPrefix = currentPrefix.replace(/\s/g, '');

    if (rawValue === rawPrefix) {
      fullInput.value = '';
    } else {
      fullInput.value = rawValue;
    }
  };

  const validatePhone = () => {
    if (!currentPrefix) {
      if (visualInput.hasAttribute('required')) {
         setVisualState('invalid', 'Phone number is required.');
         return false;
      }
      return true;
    }

    const numberPart = visualInput.value.substring(currentPrefix.length);
    
    if (numberPart.trim().length === 0) {
      if (visualInput.hasAttribute('required')) {
        setVisualState('invalid', 'Phone number is required.');
        return false;
      }
      setVisualState('neutral');
    } 
    else if (numberPart.length < 7) {
      setVisualState('invalid', 'Phone number must be at least 7 digits.');
      return false;
    } 
    else {
      setVisualState('valid');
    }
    return true;
  };

  rootEl.validate = validatePhone;

  const closeMenu = () => {
    menu.classList.add('opacity-0', 'scale-95');
    trigger.setAttribute('aria-expanded', 'false');
    menuIsOpen = false;

    if (searchInput) {
      searchInput.value = '';
      options.forEach(opt => opt.classList.remove('hidden'));
    }

    document.removeEventListener('click', handleOutsideClick);
    document.removeEventListener('keydown', handleGlobalKeydown);
    window.removeEventListener('resize', positionMenu);
    window.removeEventListener('scroll', positionMenu, true);

    setTimeout(() => {
      if (!menuIsOpen) {
        menu.classList.add('hidden');
        menu.style.position = '';
        menu.style.top = '';
        menu.style.left = '';
        menu.style.width = '';
        menu.style.bottom = '';
        menu.style.zIndex = '';
        menu.style.flexDirection = '';
        
        if (list) list.style.maxHeight = '';
      }
    }, 100);
  };

  const positionMenu = () => {
    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const nav = document.querySelector('nav.fixed.bottom-0');
    const navHeight = nav ? nav.offsetHeight : 0;

    const spaceAbove = rect.top; 
    const spaceBelow = viewportHeight - rect.bottom - navHeight;
    const estimatedHeight = 280;

    let openUpwards = false;
    
    if (spaceBelow >= estimatedHeight) {
        openUpwards = false; 
    } else if (spaceAbove >= estimatedHeight) {
        openUpwards = true; 
    } else {
        openUpwards = spaceAbove > spaceBelow;
    }

    let left = rect.left;
    let width = 288; 

    if (left + width > viewportWidth) {
      const diff = (left + width) - viewportWidth;
      left = left - diff - 10;
    }
    if (left < 0) left = 10;

    menu.style.left = `${left}px`;
    menu.style.width = `${width}px`;
    menu.style.position = 'fixed';
    menu.style.zIndex = '9999';
    menu.style.flexDirection = 'column'; 
    
    if (list) list.style.maxHeight = '';

    const searchHeight = 50; 
    const safetyMargin = 10;

    if (openUpwards) {
      menu.style.bottom = `${viewportHeight - rect.top + 4}px`;
      menu.style.top = 'auto';
      
      if (list) {
         const maxH = spaceAbove - searchHeight - safetyMargin;
         list.style.maxHeight = `${Math.min(240, maxH)}px`;
      }
    } else {
      menu.style.top = `${rect.bottom + 4}px`;
      menu.style.bottom = 'auto';
      
      if (list) {
         const maxH = spaceBelow - searchHeight - safetyMargin;
         list.style.maxHeight = `${Math.min(240, maxH)}px`;
      }
    }
  };

  const openMenu = () => {
    document.dispatchEvent(new CustomEvent('close-all-menus'));
    menu.classList.remove('hidden');
    positionMenu();

    requestAnimationFrame(() => {
      menu.classList.remove('opacity-0', 'scale-95');
    });

    trigger.setAttribute('aria-expanded', 'true');
    menuIsOpen = true;

    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleGlobalKeydown);
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);

    if (searchInput) {
      searchInput.focus();
    } else {
       const selected = options.find((opt) => opt.getAttribute('aria-selected') === 'true');
       (selected ?? options[0])?.focus();
    }
  };

  const selectOption = (option, skipInputUpdate = false) => {
    if (!option) return;

    options.forEach(opt => opt.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');

    const prefix = option.dataset.value; 
    const code = option.dataset.code; 

    display.textContent = code;
    display.classList.remove('text-slate-500');
    display.classList.add('text-slate-800');
    
    // We keep data-has-value for other logic, but visual state is now handled by classes
    trigger.dataset.hasValue = 'true';

    const oldPrefix = currentPrefix;
    currentPrefix = prefix + ' ';

    if (!skipInputUpdate) {
      if (oldPrefix && visualInput.value.startsWith(oldPrefix)) {
        const numberPart = visualInput.value.substring(oldPrefix.length);
        visualInput.value = currentPrefix + numberPart;
      } else {
        visualInput.value = currentPrefix;
      }
    }

    visualInput.disabled = false;
    if (!skipInputUpdate) visualInput.focus();
    
    syncToMaster();
    
    // Re-validate to update colors immediately
    const numberPart = visualInput.value.substring(currentPrefix.length);
    if (numberPart.length > 0) {
        validatePhone();
    } else {
        // Reset to neutral if just selected flag but haven't typed yet
        setVisualState('neutral');
    }

    closeMenu();
  };

  const handleInput = () => {
    if (!currentPrefix) return;

    let userTyped;
    if (visualInput.value.startsWith(currentPrefix)) {
      userTyped = visualInput.value.substring(currentPrefix.length);
    } else {
      const prefixDigits = currentPrefix.replace(/[^0-9]/g, '');
      const allDigits = visualInput.value.replace(/[^0-9]/g, '');
      
      if (allDigits.startsWith(prefixDigits)) {
        userTyped = allDigits.substring(prefixDigits.length);
      } else {
        userTyped = '';
      }
    }

    const cleanedUserTyped = userTyped.replace(/[^0-9]/g, '');
    visualInput.value = currentPrefix + cleanedUserTyped;

    syncToMaster();

    if (visualInput.getAttribute('aria-invalid') === 'true') {
      validatePhone(); 
    }
  };

  const handleOutsideClick = (e) => {
    if (!rootEl.contains(e.target) && !menu.contains(e.target)) closeMenu();
  };

  const handleGlobalKeydown = (e) => {
    if (e.key === 'Escape') {
      closeMenu();
      trigger.focus();
    }
    if (e.key === 'Tab') {
      closeMenu();
    }
  };

  const initialize = () => {
    const initialValue = fullInput.value;
    if (initialValue) {
      const match = options.find(opt => initialValue.startsWith(opt.dataset.value));
      
      if (match) {
        visualInput.value = initialValue.replace(match.dataset.value, match.dataset.value + ' ');
        selectOption(match, true);
        validatePhone();
      }
    }
  };

  // Search Logic
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      const term = e.target.value.toLowerCase();
      options.forEach(opt => {
        const text = (opt.dataset.text || '').toLowerCase();
        const code = (opt.dataset.code || '').toLowerCase();
        const value = (opt.dataset.value || '').toLowerCase();
        
        if (text.includes(term) || code.includes(term) || value.includes(term)) {
          opt.classList.remove('hidden');
        } else {
          opt.classList.add('hidden');
        }
      });
    });

    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const firstVisible = options.find(opt => !opt.classList.contains('hidden'));
        if (firstVisible) firstVisible.focus();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const firstVisible = options.find(opt => !opt.classList.contains('hidden'));
        if (firstVisible) {
          selectOption(firstVisible);
        }
      }
    });
  }

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (menuIsOpen) closeMenu();
    else openMenu();
  });

  options.forEach((option) => {
    option.addEventListener('click', (e) => {
      e.stopPropagation();
      selectOption(option);
    });

    // Keydown on Options
    option.addEventListener('keydown', (e) => {
      const visibleOptions = options.filter(opt => !opt.classList.contains('hidden'));
      const idx = visibleOptions.indexOf(option);
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (idx < visibleOptions.length - 1) visibleOptions[idx + 1].focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (idx > 0) visibleOptions[idx - 1].focus();
          else if (searchInput) searchInput.focus();
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          selectOption(option);
          break;
      }
    });
  });

  visualInput.addEventListener('input', handleInput);
  visualInput.addEventListener('blur', validatePhone);

  initialize();

  document.addEventListener('close-all-menus', () => {
    if (menuIsOpen) closeMenu();
  });
}